import { defaultSystemSettings, normalizeSystemSettings, type SystemSettings } from "../../domain/settings/system-settings.js";
import type { SystemSettingsRepository } from "../../repositories/system-settings-repository.js";

/**
 * Leitura INTERNA das configurações, para os módulos que as consomem
 * (numeração, notificações, importação, política de senha, END).
 *
 * Não é serviço de RPC e não confere permissão — e por isso NUNCA entra na
 * lista de serviços liberados do servidor: o `/api/rpc` expõe todos os
 * métodos de um serviço liberado, e um acessor sem conferência vazaria a
 * configuração inteira. Quem mostra configuração para uma pessoa é o
 * `SystemSettingsService`, que confere a permissão de cada ato.
 */
export class SystemSettingsProvider {
  private cached: { settings: SystemSettings; version: number; loadedAt: number } | null = null;
  /** Releitura periódica: pega gravações feitas por outro processo/aba sem depender de aviso. */
  private static readonly TTL_MS = 30_000;

  constructor(private readonly repo: SystemSettingsRepository) {}

  async get(): Promise<SystemSettings> {
    if (this.cached && Date.now() - this.cached.loadedAt < SystemSettingsProvider.TTL_MS) return this.cached.settings;
    return (await this.reload()).settings;
  }

  async reload(): Promise<{ settings: SystemSettings; version: number }> {
    const record = await this.repo.load();
    const settings = normalizeSystemSettings(record?.payload ?? null);
    const version = record?.version ?? 0;
    settings.version = version;
    settings.updatedAt = record?.updatedAt ?? null;
    settings.updatedBy = record?.updatedBy ?? null;
    this.cached = { settings, version, loadedAt: Date.now() };
    return { settings, version };
  }

  /**
   * Leitura síncrona para quem não pode esperar (ex.: filtro de notificação
   * dentro de um fluxo já em andamento). Sem nada carregado ainda, devolve os
   * PADRÕES — que são o comportamento que o sistema sempre teve.
   */
  peek(): SystemSettings {
    return this.cached?.settings ?? defaultSystemSettings();
  }

  invalidate(): void {
    this.cached = null;
  }

  /** Grava e já atualiza o cache. `false` = outra gravação chegou antes (versão mudou). */
  async save(next: SystemSettings, expectedVersion: number, meta: { updatedAt: string; updatedBy: string }): Promise<boolean> {
    const { version: _v, updatedAt: _u, updatedBy: _b, ...payload } = next;
    const ok = await this.repo.saveIfVersion(payload, expectedVersion, meta);
    this.invalidate();
    if (ok) await this.reload();
    return ok;
  }
}
